import { useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Clock, Mail, MapPin, MessageCircle, Phone } from 'lucide-react'
import { SITE, whatsappHref } from '../data/site'
import { useGsapContext, useGsapStagger } from '../hooks/useGsapReveal'
import {
  headingReveal,
  imageReveal,
  staggerChildren,
} from '../animations/gsapAnimations'
import Button from '../components/ui/Button'
import PageIntro from '../components/ui/PageIntro'
import Section from '../components/ui/Section'
import SectionHeading from '../components/ui/SectionHeading'

const PHONE_PATTERN = /^(\+92|0)?[\s-]?3\d{2}[\s-]?\d{7}$/

const schema = z.object({
  name: z.string().trim().min(2, 'Please tell us your name'),
  phone: z.string().trim().regex(PHONE_PATTERN, 'Enter a valid mobile number'),
  interest: z.string().min(1),
  message: z.string().trim().max(400).optional(),
})

type CallbackForm = z.infer<typeof schema>

const INTERESTS = [
  'Financing a car',
  'A specific vehicle',
  'Booking a test drive',
  'Something else',
] as const

const MAPS_QUERY = encodeURIComponent(`${SITE.address}`)

function Contact() {
  const cardsRef = useGsapStagger<HTMLUListElement>('[data-channel]', {
    y: 22,
    stagger: 0.07,
  })

  /* The form's fields arrive one under the other, the map wipes open from
     the edge it sits against, and the address and hours follow it. */
  const callbackRef = useGsapContext<HTMLDivElement>((scope) => {
    staggerChildren(scope, '[data-field]', { y: 18, stagger: 0.08, start: 'top 92%' })

    const map = scope.querySelector('[data-map]')
    if (map) imageReveal(map, { from: 'right' })

    staggerChildren(scope, '[data-where]', { y: 18, stagger: 0.1 })

    return headingReveal(scope)
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CallbackForm>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', phone: '', interest: INTERESTS[0], message: '' },
  })

  /* Same handling as the test-drive form: there is no endpoint yet, so the
     request is composed and handed to WhatsApp rather than dropped into a
     handler that pretends to have sent it. */
  const onSubmit: SubmitHandler<CallbackForm> = (data) => {
    const lines = [
      `Hi ${SITE.name} ${SITE.suffix}, please call me back.`,
      '',
      `Name: ${data.name}`,
      `Phone: ${data.phone}`,
      `About: ${data.interest}`,
      data.message ? `Message: ${data.message}` : '',
    ].filter(Boolean)

    window.open(whatsappHref(lines.join('\n')), '_blank', 'noopener')
  }

  const channels = [
    {
      icon: Phone,
      term: 'Phone',
      value: SITE.phone,
      href: SITE.phoneHref,
      note: 'Mon – Sun, 10:00 to 20:00',
    },
    {
      icon: MessageCircle,
      term: 'WhatsApp',
      value: SITE.phone,
      href: whatsappHref(`Hi ${SITE.name} ${SITE.suffix},`),
      note: 'Usually answered within the hour',
    },
    {
      icon: Mail,
      term: 'Email',
      value: SITE.email,
      href: `mailto:${SITE.email}`,
      note: 'For documents and formal quotes',
    },
    {
      icon: MapPin,
      term: 'Showroom',
      value: SITE.address,
      href: `https://www.google.com/maps/search/?api=1&query=${MAPS_QUERY}`,
      note: 'Open seven days a week',
    },
  ]

  const field =
    'mt-2 w-full rounded-xl border border-line bg-raised px-4 py-3.5 text-[15px] text-platinum placeholder:text-dim focus:border-lime focus:outline-none'
  const label =
    'label-sm text-steel'

  return (
    <>
      <PageIntro
        eyebrow="Contact"
        title="Come and see the cars in person"
        lede={`Open seven days in ${SITE.address}. Call, message, or send us the car you want and we will come back with every bank’s number.`}
      >
        <div className="flex flex-wrap gap-3">
          <Button
            size="lg"
            href={`https://www.google.com/maps/search/?api=1&query=${MAPS_QUERY}`}
            target="_blank"
            rel="noreferrer"
            arrow
            magnetic
          >
            Visit our showroom
          </Button>
          <Button variant="ghost" size="lg" href={SITE.phoneHref}>
            Call {SITE.phone}
          </Button>
        </div>
      </PageIntro>

      {/* ---------- channels ---------- */}
      <Section tone="ink" labelledBy="channels-title">
        <SectionHeading
          id="channels-title"
          eyebrow="Get in touch"
          title="However suits you"
        />
        <ul
          ref={cardsRef}
          className="mt-12 grid gap-px overflow-hidden rounded-xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-4"
        >
          {channels.map((channel) => (
            <li key={channel.term} data-channel className="bg-raised">
              <a
                href={channel.href}
                target={channel.href.startsWith('http') ? '_blank' : undefined}
                rel={channel.href.startsWith('http') ? 'noreferrer' : undefined}
                className="group flex h-full flex-col gap-4 p-8 transition-colors duration-300 hover:bg-surface"
              >
                <span className="icon-ring size-12 transition duration-300 group-hover:bg-lime group-hover:text-void">
                  <channel.icon
                    aria-hidden="true"
                    strokeWidth={1.6}
                    className="size-5"
                  />
                </span>
                <span className="label-sm text-dim">
                  {channel.term}
                </span>
                <span className="text-[15px] leading-snug font-medium text-platinum">
                  {channel.value}
                </span>
                <span className="mt-auto text-[12.5px] text-steel">
                  {channel.note}
                </span>
              </a>
            </li>
          ))}
        </ul>
      </Section>

      {/* ---------- callback form + map ---------- */}
      <Section
        tone="surface"
        labelledBy="callback-title"
      >
        <div
          ref={callbackRef}
          className="grid gap-12 lg:grid-cols-[1fr_1.05fr] lg:gap-16"
        >
          <div>
            <p data-heading="eyebrow" className="flex items-center gap-3 label text-lime">
              <span aria-hidden="true" data-heading="rule" className="h-px w-9 coachline" />
              Request a callback
            </p>
            <h2
              id="callback-title"
              data-heading="title"
              className="mt-5 font-display text-display-2 text-platinum"
            >
              Tell us when to call
            </h2>
            <p
              data-heading="body"
              className="mt-5 max-w-[48ch] text-[15.5px] leading-relaxed text-steel"
            >
              Leave your number and what it is about. One of the team picks it
              up and stays with your file from there.
            </p>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="mt-10 flex flex-col gap-5"
              noValidate
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div data-field>
                  <label htmlFor="cb-name" className={label}>
                    Your name
                  </label>
                  <input
                    id="cb-name"
                    {...register('name')}
                    className={field}
                    placeholder="Full name"
                  />
                  {errors.name ? (
                    <p role="alert" className="mt-2 text-[12.5px] text-mint-deep">
                      {errors.name.message}
                    </p>
                  ) : null}
                </div>

                <div data-field>
                  <label htmlFor="cb-phone" className={label}>
                    Mobile number
                  </label>
                  <input
                    id="cb-phone"
                    type="tel"
                    inputMode="tel"
                    {...register('phone')}
                    className={field}
                    placeholder="0300 1234567"
                  />
                  {errors.phone ? (
                    <p role="alert" className="mt-2 text-[12.5px] text-mint-deep">
                      {errors.phone.message}
                    </p>
                  ) : null}
                </div>
              </div>

              <div data-field>
                <label htmlFor="cb-interest" className={label}>
                  What is it about
                </label>
                <select id="cb-interest" {...register('interest')} className={field}>
                  {INTERESTS.map((interest) => (
                    <option key={interest} value={interest}>
                      {interest}
                    </option>
                  ))}
                </select>
              </div>

              <div data-field>
                <label htmlFor="cb-message" className={label}>
                  Anything else (optional)
                </label>
                <textarea
                  id="cb-message"
                  rows={4}
                  {...register('message')}
                  className={`${field} resize-none`}
                  placeholder="The car you have in mind, a budget, a time that suits you…"
                />
              </div>

              <Button type="submit" size="lg" data-field className="w-full sm:w-auto">
                Request a callback
              </Button>

              <p data-field className="text-[12px] leading-relaxed text-dim">
                This opens WhatsApp with your details written out. Nothing is
                sent until you press send there.
              </p>
            </form>
          </div>

          {/* ---------- where we are ---------- */}
          <div className="flex flex-col gap-6">
            <div data-map className="overflow-hidden rounded-xl border border-line bg-raised shadow-card">
              <iframe
                title="Map showing the Sultan Motors showroom"
                src={`https://maps.google.com/maps?q=${MAPS_QUERY}&z=14&output=embed`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="h-80 w-full border-0 grayscale invert-[0.93] hue-rotate-180 contrast-[0.88] lg:h-105"
              />
            </div>

            <dl className="grid gap-px overflow-hidden rounded-xl border border-line bg-line sm:grid-cols-2">
              <div data-where className="bg-raised p-7">
                <dt className="flex items-center gap-2 label-sm text-dim">
                  <MapPin aria-hidden="true" strokeWidth={1.6} className="size-3.5 text-lime-deep" />
                  Address
                </dt>
                <dd className="mt-3 text-[14.5px] leading-relaxed text-silver">
                  {SITE.address}
                </dd>
              </div>
              <div data-where className="bg-raised p-7">
                <dt className="flex items-center gap-2 label-sm text-dim">
                  <Clock aria-hidden="true" strokeWidth={1.6} className="size-3.5 text-lime-deep" />
                  Opening hours
                </dt>
                <dd className="mt-3 text-[14.5px] leading-relaxed text-silver">
                  {SITE.hours}
                </dd>
              </div>
            </dl>

            <Button
              variant="ghost"
              size="lg"
              href={`https://www.google.com/maps/search/?api=1&query=${MAPS_QUERY}`}
              target="_blank"
              rel="noreferrer"
              arrow
              data-where
              className="w-full"
            >
              Open in Google Maps
            </Button>
          </div>
        </div>
      </Section>
    </>
  )
}

export default Contact
